import React, { useEffect, useState, useMemo } from "react";
import "font-awesome/css/font-awesome.css";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import { connect } from "react-redux";
import _ from "lodash";
import {
  getCurrentUser,
  handleLogin,
} from "./app/common/routines/user-operations";
import { routes } from "./app/constants/routes";
import AppRoute from "./app-route";
import LandingPage from "./app/features/landing-page/landing-page";
import { useSetLoading } from "./app/common/hooks/appearance";
import CompanyDashboard from "./app/features/company-dashboard/company-dashboard";
import RequestDashboard from "./app/features/request-dashboard/request-dashboard";
import MainLayout from "./app/features/home/main-layout/main-layout";
import Unauthorized from "./app/common/components/un-authorized/un-authorized";
import NoAccess from "./app/features/no-access/no-access";
import { useMsal, MsalProvider } from "@azure/msal-react";
import { login, loginOut } from "./app/api/login";
import Profile from "./app/features/profile/profile";
import ManagePermissions from "./app/features/manage-permissions/manage-permissions";
import NoRole from "./app/features/no-role/no-role";
import Cookies from "js-cookie";
import {
  InteractionStatus,
} from "@azure/msal-browser";

const mstp = (state: any) => {
  return {
    isLoggedIn: !_.isEmpty(getCurrentUser()),
    appDataLoading: state.appearance.appDataLoading,
    tenantDetails: state.session.tenantDetails,
    userDetails: state.session.userDetails,
  };
};

const mdtp = (dispatch: any) => {
  return {
    setLoggedIn: (value: any) => {
      dispatch({
        type: "SESSION:SET_IS_LOGGED_IN",
        payload: value,
      });
    },
  };
};
/**
 * App decides between login routes, role-less routes, and authenticated routes.
 * It also handles MSAL redirect flow and triggers the backend login on first load.
 */
function App(props: any) {
  const { instance, inProgress, accounts } = useMsal();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Handle MSAL redirect results once interaction is complete
    if (inProgress === InteractionStatus.None) {
      setIsLoading(true);
      instance
        .handleRedirectPromise()
        .then(async (response) => {
          setIsLoading(true);
          if (response) {
                        const storedData = Cookies.get("userDetails") || "{}";
              const token = JSON.parse(storedData)?.fetchAPI;
            if (token) {
              setIsLoading(false);
            } else {
              try {
                // Exchange ADO access token with backend and hydrate app session
                const photoResponse = await fetch(
                  "https://graph.microsoft.com/v1.0/me/photo/$value",
                  {
                    headers: {
                      Authorization: `Bearer ${response.accessToken}`,
                    },
                  }
                );
                const blob = await photoResponse.blob();
                const imageUrl = URL.createObjectURL(blob);
                localStorage.setItem("profileImageUrl", imageUrl);
                await login({
                  adoAccessToken: response?.accessToken,
                  emailId: response?.account?.username,
                }).then(async (resp) => {
                  setIsLoading(false);
                  const roles = resp.data.data.roles;
                  if (!roles || roles.length === 0) {
                    globalThis.location.href = routes.NO_ROLE;
                    handleLogin(resp.data.data, response, response.accessToken);
                  } else {
                    handleLogin(resp.data.data, response, response.accessToken);
                  }
                });
              } catch (error: any) {
                if (error?.response?.status === 400) {
                  globalThis.location.href = routes.NO_ACCESS;
                }
                setIsLoading(false);
              }
            }
          } else {
            setIsLoading(false);
          }
        })
        .catch(() => {
          setIsLoading(false);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [instance, accounts, inProgress]);
  const funcTimeOut = () =>
    setTimeout(() => {
      localStorage.clear();
      const hostUrl = globalThis.location.origin;
      globalThis.location.href = `${hostUrl}${routes.LANDING}`;
    }, 1000);
  useEffect(() => {
    let timer: any;
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(
        async () => {
          const accounts = instance.getAllAccounts();
          const hostUrl = globalThis.location.origin;
          if (accounts.length > 0) {
            instance.clearCache();
            try {
              await loginOut();
            } catch (error) {
              console.error("Error during logout:", error);
            }

            localStorage.clear();
            sessionStorage.clear();
            Cookies.remove("userDetails");
            funcTimeOut();
          } else {
            try {
              await loginOut();
            } catch (error) {
              console.error("Error during logout:", error);
            }

            localStorage.clear();
            Cookies.remove("userDetails");
            globalThis.location.href = `${hostUrl}${routes.LANDING}`;
          }
        },
        15 * 60 * 1000,
      );
    };

    globalThis.addEventListener("mousemove", resetTimer);
    globalThis.addEventListener("keydown", resetTimer);

    resetTimer();

    return () => {
      globalThis.removeEventListener("mousemove", resetTimer);
      globalThis.removeEventListener("keydown", resetTimer);
      clearTimeout(timer);
    };
  }, []);

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const userDetails = getCurrentUser();
  const hasRoles = useMemo(() => {
    return userDetails?.roles?.length > 0;
  }, [userDetails]);

  const loginRouter = createBrowserRouter(
    createRoutesFromElements(
      <Route>
        <Route path={routes.UNAUTHORIZED} element={<Unauthorized />} />
        <Route path={routes.LANDING} element={<LandingPage />} />
        <Route path={routes.NO_ROLE} element={<NoRole isNoRole={true} />} />
        <Route path={routes.NO_ACCESS} element={<NoAccess />} />
        <Route path="*" element={<Navigate to={routes.LANDING} replace />} />
      </Route>,
    ),
  );

  const LoggedInRouter = createBrowserRouter(
    createRoutesFromElements(
      <Route>
        <Route path={routes.DASHBOARD} element={<MainLayout />}>
          <Route
            path={routes.COMPANYDASHBOARD}
            element={<CompanyDashboard />}
          />
          <Route
            path={routes.REQUESTDASHBOARD}
            element={<RequestDashboard />}
          />
          <Route path={routes.PROFILE} element={<Profile />} />
          <Route path={routes.ROLESDASHBOARD} element={<ManagePermissions />} />
        </Route>
        <Route path={routes.UNAUTHORIZED} element={<Unauthorized />} />
        <Route
          path="*"
          element={<Navigate to={routes.COMPANYDASHBOARD} replace />}
        />
      </Route>,
    ),
  );

  const NoRoleRouter = createBrowserRouter(
    createRoutesFromElements(
      <Route>
        <Route path={routes.NO_ROLE} element={<NoRole isNoRole={true} />} />
        <Route path="*" element={<Navigate to={routes.NO_ROLE} replace />} />
      </Route>,
    ),
  );

  let routerElement = <RouterProvider router={loginRouter} />;
  if (props.isLoggedIn) {
    routerElement = hasRoles ? (
      <RouterProvider router={LoggedInRouter} />
    ) : (
      <RouterProvider router={NoRoleRouter} />
    );
  }

  return <> {routerElement}</>;
}

export default connect(
  mstp,
  mdtp,
)((props: any) => {
  // Show a small splash while Redux login state is being set
  const [loading, setLoading] = useState<boolean>(true);
  useEffect(() => {
    const loggedIn = !_.isEmpty(getCurrentUser());
    props.setLoggedIn(loggedIn);
  }, []);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 50);
  }, [props.isLoggedIn]);

  return (
    <MsalProvider instance={props?.msalInstances}>
      <AppRoute>
        {loading ? <div>Loading...</div> : <App {...props} />}
      </AppRoute>
    </MsalProvider>
  );
});
